/**
 * Created by neo on 23.03.21.
 */
import { makeAutoObservable } from 'mobx';
import { DurationBefore, durationOrder, NotificationInterval, createNotificationInterval } from './NotificationInterval';
import { v4 as UUID } from 'uuid';
import { HttpBackend } from '../../../Services/Http/HttpBackend';
import { Pageable } from '../../Interfaces/Pageable';
import { EMPTY_ARRAY } from '../../../Utils/Constants';
import { SubscribableEntry } from '../../Explore/SubscribableEntry';
import { getMediaMediumOrSmallest } from '../../Media/Media';

export type EventEntrySearchParams = Pageable & {
  athleteId?: string;
  objectType?: string;
  objectId?: string;
  sourceType?: string;
  sourceId?: string;
};

export type SubscribedEventEntryJson = {
  id: string;
  athleteId: string;
  objectType?: string;
  objectId?: string;
  sourceType?: string;
  sourceId?: string;
  notificationEnabled: boolean;
  notificationIntervals: NotificationInterval[];
  groupId?: string;
  duration: string;
  dates: Date[];
};

export type SubscribedEventEntry = {
  id: string;
  athleteId: string;
  objectType?: string;
  objectId?: string;
  sourceType?: string;
  sourceId?: string;
  notificationEnabled: boolean;
  notificationIntervals: NotificationInterval[];
  groupId?: string;
  duration: string;
  dates: Date[];
  object?: SubscribableEntry;
  readonly title: string | undefined;
  readonly message: string | undefined;
  readonly image: string | undefined;
  readonly link: string | undefined;
  readonly earliestInterval: NotificationInterval | undefined;
  readonly latestInterval: NotificationInterval | undefined;
  toJS(): SubscribedEventEntryJson;
  withoutRecurring(): SubscribedEventEntry;
  getName(language?: string): string | undefined;
};

export function createSubscribedEventEntry(json?: Partial<SubscribedEventEntry>): SubscribedEventEntry {
  const entry: SubscribedEventEntry = makeAutoObservable({
    id: json?.id ?? UUID(),
    athleteId: json?.athleteId ?? '',
    objectType: json?.objectType,
    objectId: json?.objectId,
    sourceType: json?.sourceType,
    sourceId: json?.sourceId,
    notificationEnabled: json?.notificationEnabled ?? true,
    notificationIntervals: (json?.notificationIntervals ?? [{ durationBefore: DurationBefore.AT_EVENT } as any]).map((i: any) => createNotificationInterval(i)),
    groupId: json?.groupId,
    duration: json?.duration ?? 'PT1H',
    dates: (json?.dates ?? EMPTY_ARRAY).map((d: any) => new Date(d)),
    object: undefined as SubscribableEntry | undefined,

    get title(): string | undefined {
      return this.notificationIntervals.length > 0 ? this.notificationIntervals[0].title : undefined;
    },

    get message(): string | undefined {
      return this.notificationIntervals.length > 0 ? this.notificationIntervals[0].message : undefined;
    },

    get image(): string | undefined {
      return this.object?.media ? getMediaMediumOrSmallest(this.object.media) : undefined;
    },

    get link(): string | undefined {
      return this.object ? `https://coach.kinastic.com/explore/${this.object.type}/${this.object.id}?eventId=${this.id}` : undefined;
    },

    get earliestInterval(): NotificationInterval | undefined {
      return this.notificationIntervals.slice().sort((a, b) => durationOrder.indexOf(b.durationBefore) - durationOrder.indexOf(a.durationBefore))[0];
    },

    get latestInterval(): NotificationInterval | undefined {
      return this.notificationIntervals.slice().sort((a, b) => durationOrder.indexOf(a.durationBefore) - durationOrder.indexOf(b.durationBefore))[0];
    },

    toJS(): SubscribedEventEntryJson {
      return {
        id: this.id,
        athleteId: this.athleteId,
        objectType: this.objectType,
        objectId: this.objectId,
        sourceType: this.sourceType,
        sourceId: this.sourceId,
        notificationEnabled: this.notificationEnabled,
        notificationIntervals: this.notificationIntervals,
        groupId: this.groupId,
        duration: this.duration,
        dates: this.dates,
      };
    },

    withoutRecurring(): SubscribedEventEntry {
      return createSubscribedEventEntry(this);
    },

    getName(language: string = 'de'): string | undefined {
      return this.object?.translatedName;
    },
  });

  return entry;
}

export const SubscribedEventEntryApi = {
  find(params: EventEntrySearchParams): Promise<SubscribedEventEntry[]> {
    return HttpBackend.get('/coach/schedule/event/subscription', params).then((res) =>
      res.map((r: any) => createSubscribedEventEntry(r)),
    );
  },

  findExisting(params: EventEntrySearchParams): Promise<SubscribedEventEntry | undefined> {
    return HttpBackend.get('/coach/schedule/event/subscription', Object.assign(params, { size: 1 })).then((res) =>
      res[0] ? createSubscribedEventEntry(res[0]) : undefined,
    );
  },

  findOne(id: string): Promise<SubscribedEventEntry> {
    return HttpBackend.get(`/coach/schedule/event/subscription/${id}`).then((res) => createSubscribedEventEntry(res));
  },

  save(entry: SubscribedEventEntry): Promise<SubscribedEventEntry> {
    return HttpBackend.post('/coach/schedule/event/subscription', entry).then((res) =>
      createSubscribedEventEntry(res),
    );
  },

  delete(id: string): Promise<void> {
    return HttpBackend.delete(`/coach/schedule/event/subscription/${id}`);
  },
};
